import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React, {memo, useState} from 'react';
import DisplayImage from '@/components/Ui-components/display-image';
import parse from 'html-react-parser';
import {borderRadius, colors, fontWeight} from '@/styles/tokens';

// ─── sx constants ─────────────────────────────────────────────────────────────

const tabBtnBaseSx = {
    width: 190,
    height: 76,
    borderRadius: borderRadius.xl,
    border: '2px solid #1f3f7a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
    userSelect: 'none',
    flexShrink: 0
} as const;

const activeTabSx = {
    ...tabBtnBaseSx,
    bgcolor: colors.primary.deep,
    height: 80,
    boxShadow: '0px 20px 25px rgba(0,0,0,0.1), 0px 8px 10px rgba(0,0,0,0.1)'
} as const;

const inactiveTabSx = {
    ...tabBtnBaseSx,
    bgcolor: colors.background.default,
    boxShadow: '0px 4px 6px rgba(0,0,0,0.1), 0px 2px 4px rgba(0,0,0,0.1)'
} as const;

const activeStepSx = {
    bgcolor: colors.background.default,
    borderRadius: borderRadius.lg,
    boxShadow: '0px 3px 12px rgba(0,0,0,0.11)',
    px: 3,
    py: 2,
    cursor: 'pointer'
} as const;

const inactiveStepSx = {
    px: 3,
    py: 2,
    cursor: 'pointer'
} as const;

// ─── Component ────────────────────────────────────────────────────────────────

const EverythingMobile = (props: any) => {
    const {heading, description, contentData, webContentData} = props;
    const [activeTab, setActiveTab] = useState<'mobile' | 'web'>('mobile');
    const [activeIndex, setActiveIndex] = useState(0);

    const handleTabChange = (tab: 'mobile' | 'web') => {
        setActiveTab(tab);
        setActiveIndex(0);
    };

    const activeData = activeTab === 'web' && webContentData ? webContentData : contentData;
    const inactiveData = activeTab === 'web' ? contentData : webContentData;

    return (
        <Box py={{xs: 6, md: 8}}>
            <MUIContainer>
                {/* Heading block */}
                <Box textAlign="center" maxWidth={1047} margin="0 auto" mb={5}>
                    <Typography variant="h2" component="h2" mb={3} fontSize={{xs: 30, sm: 52}} fontWeight={800} className="font-headers">
                        {heading}
                    </Typography>
                    {description && (
                        <Typography
                            className="font-regular-text remove-underline"
                            sx={{fontSize: 16, lineHeight: '28px', color: '#000', maxWidth: 941, mx: 'auto'}}
                        >
                            {parse(description)}
                        </Typography>
                    )}
                </Box>

                {/* Tab toggle */}
                <Box display="flex" justifyContent="center" alignItems="flex-end" gap={2.5} mb={5}>
                    <Box sx={activeTab === 'mobile' ? activeTabSx : inactiveTabSx} onClick={() => handleTabChange('mobile')}>
                        <Typography
                            className="font-headers"
                            sx={{
                                fontWeight: fontWeight.bold,
                                fontSize: 18,
                                color: activeTab === 'mobile' ? '#fff' : '#1f3f7a',
                                letterSpacing: '-0.44px'
                            }}
                        >
                            Mobile App
                        </Typography>
                        {activeTab === 'mobile' && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 6,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 67,
                                    height: 4,
                                    bgcolor: '#fff',
                                    borderRadius: 999
                                }}
                            />
                        )}
                    </Box>
                    <Box sx={activeTab === 'web' ? activeTabSx : inactiveTabSx} onClick={() => handleTabChange('web')}>
                        <Typography
                            className="font-headers"
                            sx={{
                                fontWeight: fontWeight.bold,
                                fontSize: 18,
                                color: activeTab === 'web' ? '#fff' : '#1f3f7a',
                                letterSpacing: '-0.44px'
                            }}
                        >
                            Web Platform
                        </Typography>
                        {activeTab === 'web' && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 6,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 67,
                                    height: 4,
                                    bgcolor: '#fff',
                                    borderRadius: 999
                                }}
                            />
                        )}
                    </Box>
                </Box>

                {/* Content card */}
                <Box sx={{bgcolor: colors.background.subtle, borderRadius: '30px', px: {xs: 3, md: 6}, py: {xs: 5, md: 7}}}>
                    <Grid container spacing={activeTab === 'web' ? 8 : 4} alignItems="center">
                        {/* Steps list */}
                        <Grid item xs={12} md={activeTab === 'web' ? 4 : 5}>
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                {activeData?.map((item: any, index: number) => (
                                    <Box
                                        key={index}
                                        sx={activeIndex === index ? activeStepSx : inactiveStepSx}
                                        onClick={() => setActiveIndex(index)}
                                    >
                                        <Typography
                                            className="font-headers"
                                            sx={{
                                                fontSize: {xs: 14, sm: 18},
                                                fontWeight: activeIndex === index ? 700 : 500,
                                                color: activeIndex === index ? '#000' : 'rgba(0,0,0,0.45)',
                                                lineHeight: '26px'
                                            }}
                                        >
                                            {item.heading}
                                        </Typography>
                                    </Box>
                                ))}
                                {/* Inactive tab's step headings, kept in the server HTML so
                                    both Mobile and Web tab content is crawlable (SEO #33). */}
                                {inactiveData && (
                                    <Box sx={{display: 'none'}} aria-hidden="true">
                                        {inactiveData.map((item: any, index: number) => (
                                            <Typography key={index} className="font-headers">
                                                {item.heading}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* Image mockup */}
                        <Grid item xs={12} md={activeTab === 'web' ? 7 : 7}>
                            {activeTab === 'web' ? (
                                /* Screen / browser mockup — fixed 16:9 frame */
                                <Box
                                    sx={{
                                        width: '100%',
                                        borderRadius: borderRadius.lg,
                                        overflow: 'hidden',
                                        boxShadow: '0px 16px 48px rgba(0,0,0,0.18)',
                                        border: '1px solid #d0d9e8'
                                    }}
                                >
                                    {/* Browser chrome bar */}
                                    <Box
                                        sx={{
                                            bgcolor: '#e8ecf2',
                                            px: 2,
                                            py: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            borderBottom: '1px solid #c8d0dc',
                                            flexShrink: 0
                                        }}
                                    >
                                        <Box sx={{width: 10, height: 10, borderRadius: borderRadius.circle, bgcolor: '#ff5f57'}} />
                                        <Box sx={{width: 10, height: 10, borderRadius: borderRadius.circle, bgcolor: '#ffbd2e'}} />
                                        <Box sx={{width: 10, height: 10, borderRadius: borderRadius.circle, bgcolor: '#28c840'}} />
                                    </Box>
                                    {/* Fixed 16:9 viewport — image fills this, frame never resizes */}
                                    <Box sx={{position: 'relative', width: '100%', paddingTop: '56.25%'}}>
                                        <DisplayImage
                                            src={activeData[activeIndex].src}
                                            alt={activeData[activeIndex].heading}
                                            fill
                                            sizes="(max-width: 600px) 100vw, (max-width: 900px) 100vw, 60vw"
                                            style={{objectFit: 'cover', objectPosition: 'top left'}}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                /* Phone mockup */
                                <Box display="flex" justifyContent="center" alignItems="center">
                                    <Box sx={{width: {xs: 260, sm: 300, md: 340}}}>
                                        <DisplayImage
                                            src={activeData[activeIndex].src}
                                            alt={activeData[activeIndex].heading}
                                            width={591}
                                            height={1068}
                                            sizes="(max-width: 600px) 260px, (max-width: 900px) 300px, 340px"
                                            style={{width: '100%', height: 'auto', display: 'block'}}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(EverythingMobile);
